import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import EventFilters from './components/EventFilters';
import EventTable from './components/EventTable';
import EventPagination from './components/EventPagination';
import EventStats from './components/EventStats';
import ExportModal from './components/ExportModal';
import DeleteEventModal from './components/DeleteEventModal';
import BulkDeleteModal from './components/BulkDeleteModal';
import { useEvents } from '../../hooks/useEvents';

const EventManagement = () => {
  const navigate = useNavigate();
  const { userProfile, isHost, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, event: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ isOpen: false, events: [] });

  // Use events hook for delete functionality
  const { deleteEvent } = useEvents();

  // Fetch events from Supabase
  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        ?.from('events')
        ?.select(`
          *,
          host:user_profiles!events_host_id_fkey(
            id,
            full_name,
            email
          )
        `);

      // If user is a host (not admin), only show their own events
      if (isHost && !isAdmin) {
        query = query?.eq('host_id', userProfile?.id);
      }

      const { data, error: fetchError } = await query?.order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // Transform data to match the expected format
      const transformedEvents = data?.map(event => ({
        ...event,
        name: event?.title || 'Untitled Event',
        date: event?.event_date,
        location: event?.location || 'TBD',
        category: event?.category || 'other',
        ticketsSold: 0, // Will be calculated from bookings
        totalTickets: event?.max_capacity || 0,
        revenue: 0, // Will be calculated from bookings
        attendees: 0, // Will be calculated from bookings
        description: event?.description || '',
        image: event?.image_url // Map image_url from database to image for component
      })) || [];

      setEvents(transformedEvents);
      setFilteredEvents(transformedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err?.message || 'Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile) {
      fetchEvents();
    }
  }, [userProfile, isHost, isAdmin]);

  // Calculate stats from actual data
  const calculateStats = () => {
    const totalEvents = events?.length || 0;
    const activeEvents = events?.filter(event => 
      ['published', 'live']?.includes(event?.status)
    )?.length || 0;
    const totalRevenue = events?.reduce((sum, event) => sum + (event?.revenue || 0), 0);
    const totalAttendees = events?.reduce((sum, event) => sum + (event?.attendees || 0), 0);

    return {
      totalEvents,
      activeEvents,
      totalRevenue,
      totalAttendees,
      eventsChange: 0, // Could be calculated with historical data
      activeChange: 0,
      revenueChange: 0,
      attendeesChange: 0
    };
  };

  const stats = calculateStats();

  // Filtering logic
  const handleFiltersChange = (filters) => {
    let filtered = [...events];

    if (filters?.search) {
      filtered = filtered?.filter(event =>
        event?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        event?.location?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        event?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase())
      );
    }

    if (filters?.status) {
      filtered = filtered?.filter(event => event?.status === filters?.status);
    }

    if (filters?.category) {
      filtered = filtered?.filter(event => event?.category === filters?.category);
    }

    if (filters?.revenueMin) {
      filtered = filtered?.filter(event => event?.revenue >= parseFloat(filters?.revenueMin));
    }

    if (filters?.revenueMax) {
      filtered = filtered?.filter(event => event?.revenue <= parseFloat(filters?.revenueMax));
    }

    setFilteredEvents(filtered);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilteredEvents(events);
    setCurrentPage(1);
  };

  // Sorting logic
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredEvents]?.sort((a, b) => {
      if (key === 'date' || key === 'created_at') {
        const dateA = new Date(a?.[key]);
        const dateB = new Date(b?.[key]);
        return direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (typeof a?.[key] === 'number') {
        return direction === 'asc' ? a?.[key] - b?.[key] : b?.[key] - a?.[key];
      }
      
      const valueA = a?.[key]?.toString()?.toLowerCase() || '';
      const valueB = b?.[key]?.toString()?.toLowerCase() || '';
      
      if (direction === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0;
      }
    });

    setFilteredEvents(sorted);
    setSortConfig({ key, direction });
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredEvents?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEvents = filteredEvents?.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Event actions
  const handleEventAction = (eventId, action) => {
    switch (action) {
      case 'edit': navigate('/event-creation', { state: { eventId } });
        break;
      case 'view': navigate('/event-details', { state: { eventId } });
        break;
      case 'attendees': navigate('/attendee-management', { state: { eventId } });
        break;
      case 'duplicate':
        // Handle duplicate logic
        console.log('Duplicating event:', eventId);
        break;
      case 'analytics': navigate('/host-dashboard', { state: { eventId } });
        break;
      case 'archive':
        // Handle archive logic
        console.log('Archiving event:', eventId);
        break;
      case 'delete':
        const eventToDelete = events?.find(e => e?.id === eventId);
        setDeleteModal({ isOpen: true, event: eventToDelete });
        break;
      default:
        break;
    }
  };

  const handleBulkAction = (action) => {
    if (action === 'delete') {
      const eventsToDelete = events?.filter(e => selectedEvents?.includes(e?.id));
      setBulkDeleteModal({ isOpen: true, events: eventsToDelete });
    } else {
      console.log(`Bulk ${action} for events:`, selectedEvents);
      // Handle other bulk actions
      setSelectedEvents([]);
    }
  };

  const handleExport = (exportConfig) => {
    console.log('Exporting events with config:', exportConfig);
    // Handle export logic
  };

  // Delete single event
  const handleDeleteEvent = async () => {
    if (!deleteModal?.event) return;

    try {
      setIsDeleting(true);
      const { success, error } = await deleteEvent(deleteModal?.event?.id);
      
      if (success) {
        // Remove from local state
        setEvents(prev => prev?.filter(e => e?.id !== deleteModal?.event?.id));
        setFilteredEvents(prev => prev?.filter(e => e?.id !== deleteModal?.event?.id));
        setDeleteModal({ isOpen: false, event: null });
        
        // Show success message (you could add a toast notification here)
        console.log('Event deleted successfully');
      } else {
        console.error('Failed to delete event:', error);
        // Show error message
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Delete multiple events
  const handleBulkDeleteEvents = async () => {
    if (bulkDeleteModal?.events?.length === 0) return;

    try {
      setIsDeleting(true);
      const deletePromises = bulkDeleteModal?.events?.map(event => deleteEvent(event?.id));
      const results = await Promise.all(deletePromises);
      
      const successfulDeletes = results?.filter(r => r?.success);
      const failedDeletes = results?.filter(r => !r?.success);
      
      if (successfulDeletes?.length > 0) {
        // Remove successfully deleted events from local state
        const deletedIds = bulkDeleteModal?.events?.filter((_, index) => results?.[index]?.success)?.map(e => e?.id);
        
        setEvents(prev => prev?.filter(e => !deletedIds?.includes(e?.id)));
        setFilteredEvents(prev => prev?.filter(e => !deletedIds?.includes(e?.id)));
        setSelectedEvents([]);
        setBulkDeleteModal({ isOpen: false, events: [] });
        
        console.log(`Successfully deleted ${successfulDeletes?.length} events`);
      }
      
      if (failedDeletes?.length > 0) {
        console.error(`Failed to delete ${failedDeletes?.length} events`);
      }
    } catch (error) {
      console.error('Error deleting events:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64">
          <Icon name="AlertCircle" size={48} className="text-destructive mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Failed to load events</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={fetchEvents}>
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Try Again
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="px-6 py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Event Management</h1>
                <p className="text-muted-foreground mt-2">
                  Manage and oversee all your events from one centralized dashboard
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setIsExportModalOpen(true)}
                >
                  <Icon name="Download" size={16} className="mr-2" />
                  Export
                </Button>
                <Button asChild>
                  <Link to="/event-creation">
                    <Icon name="Plus" size={16} className="mr-2" />
                    Create Event
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-8">
          {/* Stats */}
          <EventStats stats={stats} />

          {/* Filters */}
          <EventFilters
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            appliedFilters={{}}
          />

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-foreground">
                Events ({filteredEvents?.length})
              </h2>
              {selectedEvents?.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {selectedEvents?.length} selected
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => handleSort('created_at')}>
                <Icon name="Calendar" size={14} className="mr-1" />
                Sort by Date
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleSort('revenue')}>
                <Icon name="DollarSign" size={14} className="mr-1" />
                Sort by Revenue
              </Button>
            </div>
          </div>

          {/* Events Table */}
          {filteredEvents?.length > 0 ? (
            <>
              <EventTable
                events={paginatedEvents}
                onSort={handleSort}
                sortConfig={sortConfig}
                onBulkAction={handleBulkAction}
                selectedEvents={selectedEvents}
                onEventSelect={setSelectedEvents}
                onEventAction={handleEventAction}
              />

              {/* Pagination */}
              <div className="mt-6">
                <EventPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={filteredEvents?.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </div>
            </>
          ) : (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No events found</h3>
              <p className="text-muted-foreground mb-6">
                {events?.length === 0 
                  ? "You haven't created any events yet. Start by creating your first event." :"No events match your current filters. Try adjusting your search criteria."
                }
              </p>
              {events?.length === 0 ? (
                <Button asChild>
                  <Link to="/event-creation">
                    <Icon name="Plus" size={16} className="mr-2" />
                    Create Your First Event
                  </Link>
                </Button>
              ) : (
                <Button onClick={handleClearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Export Modal */}
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
        />

        {/* Delete Event Modal */}
        <DeleteEventModal
          isOpen={deleteModal?.isOpen}
          onClose={() => setDeleteModal({ isOpen: false, event: null })}
          onConfirm={handleDeleteEvent}
          event={deleteModal?.event}
          isDeleting={isDeleting}
        />

        {/* Bulk Delete Modal */}
        <BulkDeleteModal
          isOpen={bulkDeleteModal?.isOpen}
          onClose={() => setBulkDeleteModal({ isOpen: false, events: [] })}
          onConfirm={handleBulkDeleteEvents}
          events={bulkDeleteModal?.events}
          isDeleting={isDeleting}
        />
      </div>
    </Layout>
  );
};

export default EventManagement;